/**
 * UpdateOrgConfirmDialog: confirm gate before reassigning a user to or off
 * the iuvo Platform organization. Crossing that boundary grants or revokes
 * Platform Admin status, so we hold for an explicit confirm and surface the
 * audit-log promise up front.
 *
 * On the Carbon ConfirmDialog (an xs Modal: the question as its title, the
 * body, Cancel and the confirm button). Not `danger`: revoking is recoverable
 * and routine, so the confirm stays the ordinary primary button, not Coral.
 *
 * Presentational only: the parent owns the API call via `onConfirm` + the
 * `isPending` flag. Escape, the scrim and Close fire `onCancel`, and are
 * inert while the request is in flight (ConfirmDialog's `loading`).
 */

import { ConfirmDialog } from '@/components/carbon/ConfirmDialog'

export type UpdateOrgConfirmMode = 'promote' | 'demote'

interface Props {
  /** Controls visibility. Parent toggles this; the dialog itself never closes. */
  open: boolean
  mode: UpdateOrgConfirmMode
  /** Used in the named-subject body sentence. */
  userEmail: string
  /** Display name of the org the user is moving FROM. Surfaced for context. */
  fromOrganizationName: string
  /** Display name of the org the user is moving TO. Surfaced for context. */
  toOrganizationName: string
  /** True while the parent's API call is in flight. Disables both buttons. */
  isPending: boolean
  /** Fires on Escape, the scrim, Close, and the Cancel button. */
  onCancel: () => void
  /** Fires on the primary button. Parent runs the mutation. */
  onConfirm: () => void
}

export default function UpdateOrgConfirmDialog({
  open,
  mode,
  userEmail,
  fromOrganizationName,
  toOrganizationName,
  isPending,
  onCancel,
  onConfirm,
}: Props) {
  const isPromote = mode === 'promote'

  const title = isPromote
    ? 'This promotes them to Platform Admin.'
    : 'This revokes their Platform Admin access.'

  const eventType = isPromote ? 'PlatformAdminGranted' : 'PlatformAdminRevoked'

  const consequence = isPromote
    ? `${userEmail} will gain platform-wide admin access on the next sign-in.`
    : `${userEmail} will lose platform-wide admin access on the next sync.`

  return (
    <ConfirmDialog
      open={open}
      title={title}
      confirmLabel={isPromote ? 'Promote' : 'Revoke'}
      loading={isPending}
      onConfirm={onConfirm}
      onCancel={onCancel}
      body={
        <div className="flex flex-col gap-3">
          <p className="m-0">{consequence}</p>
          <p className="m-0">
            Guardian will log the change as{' '}
            <code className="bg-layer-selected px-1 font-mono text-helper text-text-primary">{eventType}</code>{' '}
            against your name.
          </p>

          {/* Org-transfer row, only when the org actually changes. A
              role-only threshold cross (e.g. Change role within the
              Platform org) passes from === to, where an "X to X" row is
              noise, so we suppress it. */}
          {fromOrganizationName !== toOrganizationName && (
            <p className="m-0 bg-layer-1 px-3 py-2 text-helper text-text-secondary">
              <b className="font-semibold text-text-primary">{fromOrganizationName}</b>
              <span className="mx-2">to</span>
              <b className="font-semibold text-text-primary">{toOrganizationName}</b>
            </p>
          )}
        </div>
      }
    />
  )
}
